/**
 * CreateTaskModal - 创建任务弹窗
 * 采用底部 Popup 模式 + 内部页面横移切换
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { Popup, SafeArea, Toast } from 'antd-mobile';
import { X, ChevronLeft } from 'lucide-react';
import { AgentChat, type StructuredOutput, type TaskConfigData } from '../../agent';
import type { Category, CheckInUnit } from '../../types';
import { useScene, useTaskContext, useCultivation } from '../../contexts';
import { getNextThemeColor } from '../../constants';
import {
  calculateDailyPointsCap,
  calculateTaskCreationCostWithDiscount,
  calculateTotalCompletionReward,
} from '../../utils/spiritJadeCalculator';
import { createTask } from '../../utils/migration';
import { getCurrentDate } from '../../utils';
import { usePageStack, useSwipeBack } from '../../panels/settings/hooks';
import { CycleSettingsPage, TypeSelectPage, ConfigPage } from './pages';
import { StepProgressBar } from './components';
import { InsufficientJadePopup } from '../../components';
import type { CreateTaskModalProps } from './types';
import type { CreateTaskModalState } from './modalTypes';
import { createInitialState } from './modalTypes';
import styles from './styles.module.css';

// 初始页面 - 调整为类型选择
const INITIAL_PAGE = { id: 'type', title: '任务类型' };

// 页面步骤映射 - 调整顺序：类型 -> 配置 -> 周期预览
const PAGE_STEP_MAP: Record<string, number> = {
  type: 1,
  config: 2,
  cycle: 3,
};

export default function CreateTaskModal({
  visible,
  onClose,
  initialData
}: CreateTaskModalProps) {
  const { normal } = useScene();
  const { addTask } = useTaskContext();
  const { canSpendSpiritJade, spendSpiritJade, spiritJadeData } = useCultivation();

  const { hasMainlineTask, sidelineTasks, archivedTasks } = normal;

  // 任务类别
  const [taskCategory, setTaskCategory] = useState<'MAINLINE' | 'SIDELINE'>('MAINLINE');

  // 统一状态管理
  const [state, setState] = useState<CreateTaskModalState>(() => createInitialState(getCurrentDate()));

  // 页面栈管理
  const { stack, push, pop, canGoBack, reset } = usePageStack(INITIAL_PAGE);

  // 页面动画状态
  const [pageAnimationState, setPageAnimationState] = useState<'idle' | 'entering' | 'exiting'>('idle');
  // 动画计数器，用于强制重新触发 CSS 动画
  const [animationKey, setAnimationKey] = useState(0);
  // AI 模式状态
  const [isAIMode, setIsAIMode] = useState(false);
  // 灵玉不足弹窗状态
  const [insufficientJadePopup, setInsufficientJadePopup] = useState<{
    visible: boolean;
    requiredAmount: number;
  }>({ visible: false, requiredAmount: 0 });
  // AI 模式下滑关闭的拖拽状态
  const [aiDragY, setAiDragY] = useState(0);
  const [isDraggingAI, setIsDraggingAI] = useState(false);
  const aiTouchStartY = useRef(0);
  const aiContainerRef = useRef<HTMLDivElement>(null);
  // 页面元素 refs，用于强制触发动画
  const pageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const containerRef = useRef<HTMLDivElement>(null);

  // AI 模式下滑手势处理
  const handleAITouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    aiTouchStartY.current = touch.clientY;
    setIsDraggingAI(true);
  }, []);

  const handleAITouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDraggingAI) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - aiTouchStartY.current;
    // 只允许向下拖拽
    if (deltaY > 0) {
      setAiDragY(deltaY);
    }
  }, [isDraggingAI]);

  const handleAITouchEnd = useCallback(() => {
    if (!isDraggingAI) return;
    setIsDraggingAI(false);
    // 如果拖拽超过 80px，关闭 AI 模式
    if (aiDragY > 80) {
      setIsAIMode(false);
    }
    setAiDragY(0);
  }, [isDraggingAI, aiDragY]);

  // 处理面板显示
  useEffect(() => {
    if (visible) {
      setPageAnimationState('idle');
      setTaskCategory(hasMainlineTask ? 'SIDELINE' : 'MAINLINE');

      // 如果有 initialData，应用预填数据并直接跳转到最后一步
      if (initialData) {
        const baseState = createInitialState(getCurrentDate());
        const newState = {
          ...baseState,
          taskTitle: initialData.title || '',
          selectedType: initialData.category || null,
          totalDays: initialData.totalDays || baseState.totalDays,
          cycleDays: initialData.cycleDays || baseState.cycleDays,
        };

        // 应用数值型配置
        if (initialData.numericConfig) {
          newState.numericDirection = initialData.numericConfig.direction;
          newState.numericUnit = initialData.numericConfig.unit;
          newState.startValue = String(initialData.numericConfig.startValue);
          newState.targetValue = String(initialData.numericConfig.targetValue);
        }

        // 应用清单型配置
        if (initialData.checklistItems && initialData.checklistItems.length > 0) {
          newState.totalItems = String(initialData.checklistItems.length);
          newState.checklistItems = initialData.checklistItems;
        }

        // 应用打卡型配置
        if (initialData.checkInConfig) {
          newState.checkInUnit = initialData.checkInConfig.unit;
          if (initialData.checkInConfig.dailyMax) {
            if (initialData.checkInConfig.unit === 'DURATION') {
              newState.dailyTargetMinutes = String(initialData.checkInConfig.dailyMax);
            } else if (initialData.checkInConfig.unit === 'TIMES') {
              newState.dailyMaxTimes = String(initialData.checkInConfig.dailyMax);
            } else if (initialData.checkInConfig.unit === 'QUANTITY' && initialData.checkInConfig.valueUnit) {
              newState.dailyTargetValue = String(initialData.checkInConfig.dailyMax);
              newState.valueUnit = initialData.checkInConfig.valueUnit;
            }
          }
        }

        setState(newState);
        setIsAIMode(false);
        reset();
        // 直接跳转到最后一步（周期确认页）
        push({ id: 'cycle', title: '确认创建' });
        Toast.show({ content: 'AI 已生成配置，确认后即可创建', icon: 'success' });
      } else {
        setState(createInitialState(getCurrentDate()));
        setIsAIMode(false);
        reset();
      }
    }
  }, [visible, hasMainlineTask, reset, initialData, push]);

  // 处理 AI 生成的任务配置
  const handleAIStructuredOutput = useCallback((output: StructuredOutput) => {
    if (output.type === 'TASK_CONFIG') {
      const config = output.data as TaskConfigData;
      // 将 AI 生成的配置完整应用到表单
      setState(s => {
        const newState = {
          ...s,
          taskTitle: config.title,
          totalDays: config.totalDays,
          cycleDays: config.cycleDays,
          selectedType: config.category,
        };

        // 应用数值型配置
        if (config.numericConfig) {
          newState.numericDirection = config.numericConfig.direction;
          newState.numericUnit = config.numericConfig.unit;
          newState.startValue = String(config.numericConfig.startValue);
          newState.targetValue = String(config.numericConfig.targetValue);
        }

        // 应用清单型配置
        if (config.checklistItems && config.checklistItems.length > 0) {
          newState.totalItems = String(config.checklistItems.length);
          newState.checklistItems = config.checklistItems;
        }

        // 应用打卡型配置
        if (config.checkInConfig) {
          newState.checkInUnit = config.checkInConfig.unit;
          if (config.checkInConfig.dailyMax) {
            if (config.checkInConfig.unit === 'DURATION') {
              newState.dailyTargetMinutes = String(config.checkInConfig.dailyMax);
            } else if (config.checkInConfig.unit === 'TIMES') {
              newState.dailyMaxTimes = String(config.checkInConfig.dailyMax);
            } else if (config.checkInConfig.unit === 'QUANTITY' && config.checkInConfig.valueUnit) {
              newState.dailyTargetValue = String(config.checkInConfig.dailyMax);
              newState.valueUnit = config.checkInConfig.valueUnit;
            }
          }
        }

        return newState;
      });
      // 切换回表单模式，直接跳转到最后一步（周期预览页）
      setIsAIMode(false);
      reset();
      push({ id: 'cycle', title: '确认创建' });
      Toast.show({ content: '配置已生成，确认后即可创建' });
    }
  }, [push, reset]);

  // 关闭处理
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // 记录需要执行入场动画的新页面 ID
  const pendingEnterPageId = useRef<string | null>(null);

  // 导航到下一页
  const handleNavigate = useCallback((pageId: string, title: string) => {
    pendingEnterPageId.current = pageId;
    setAnimationKey(k => k + 1);
    setPageAnimationState('entering');
    push({ id: pageId, title });
    setTimeout(() => setPageAnimationState('idle'), 300);
  }, [push]);

  // 返回上一页
  const handleBack = useCallback((skipAnimation?: boolean) => {
    if (canGoBack) {
      if (skipAnimation) {
        pop();
        setPageAnimationState('idle');
      } else {
        setPageAnimationState('exiting');
        setTimeout(() => {
          pop();
          setPageAnimationState('idle');
        }, 300);
      }
    } else {
      handleClose();
    }
  }, [canGoBack, pop, handleClose]);

  // 手势返回支持 - 只有子页面支持手势返回，第一个页面禁用手势（防止误关闭）
  const { pageRef: subPageRef, bindEvents: bindSubPageEvents } = useSwipeBack({
    onBack: () => handleBack(true),
    enabled: canGoBack,
  });

  // 获取页面 ref - 只有子页面需要绑定手势 ref
  const getPageRef = (index: number, pageId: string) => {
    if (index !== stack.length - 1) return undefined;
    // 第一个页面 'type' 不绑定手势，返回 undefined
    return pageId === 'type' ? undefined : subPageRef;
  };

  // 提交任务
  const handleSubmit = useCallback(() => {
    if (!state.taskTitle.trim() || !state.selectedType) {
      Toast.show({ icon: 'fail', content: '请填写完整信息' });
      return;
    }

    const isMainline = taskCategory === 'MAINLINE';

    // 动态计算灵玉消耗（含折扣）
    const taskType = isMainline ? 'mainline' : 'sidelineA';
    const checkInUnit: CheckInUnit = state.selectedType === 'CHECK_IN'
      ? state.checkInUnit
      : 'TIMES';
    const dailyCap = calculateDailyPointsCap(taskType, checkInUnit);
    const totalReward = calculateTotalCompletionReward(dailyCap, state.totalDays);
    const hasSideline = sidelineTasks.length > 0;
    const discount = calculateTaskCreationCostWithDiscount(
      totalReward,
      isMainline,
      hasMainlineTask,
      hasSideline,
      archivedTasks
    );
    const requiredJade = discount.discountedCost;

    if (!canSpendSpiritJade(requiredJade)) {
      setInsufficientJadePopup({
        visible: true,
        requiredAmount: requiredJade,
      });
      return;
    }

    const cycleInfo = {
      totalCycles: Math.floor(state.totalDays / state.cycleDays),
    };

    // 构建任务数据
    let numericConfig;
    let checklistConfig;
    let checkInConfig;

    if (state.selectedType === 'NUMERIC') {
      const start = parseFloat(state.startValue);
      const target = parseFloat(state.targetValue);
      if (isNaN(start) || isNaN(target)) {
        Toast.show({ icon: 'fail', content: '请输入有效的数值' });
        return;
      }
      const totalChange = Math.abs(target - start);
      numericConfig = {
        direction: state.numericDirection,
        unit: state.numericUnit,
        startValue: start,
        targetValue: target,
        perCycleTarget: totalChange / cycleInfo.totalCycles,
      };
    } else if (state.selectedType === 'CHECKLIST') {
      const items = parseInt(state.totalItems);
      if (isNaN(items) || items < 1) {
        Toast.show({ icon: 'fail', content: '请输入有效的清单项数量' });
        return;
      }
      const filledItems = state.checklistItems.filter(item => item.trim());
      checklistConfig = {
        totalItems: items,
        perCycleTarget: Math.ceil(items / cycleInfo.totalCycles),
        items: filledItems.map((title, index) => ({
          id: `item_${Date.now()}_${index}`,
          title,
          status: 'PENDING' as const,
          cycle: Math.floor(index / Math.ceil(items / cycleInfo.totalCycles)) + 1,
        })),
      };
    } else if (state.selectedType === 'CHECK_IN') {
      checkInConfig = {
        unit: state.checkInUnit,
        allowMultiplePerDay: state.allowMultiple,
        weekendExempt: state.weekendExempt,
        perCycleTarget: 0,
        dailyMaxTimes: undefined as number | undefined,
        cycleTargetTimes: undefined as number | undefined,
        dailyTargetMinutes: undefined as number | undefined,
        cycleTargetMinutes: undefined as number | undefined,
        dailyTargetValue: undefined as number | undefined,
        cycleTargetValue: undefined as number | undefined,
        valueUnit: undefined as string | undefined,
      };

      if (state.checkInUnit === 'TIMES') {
        const maxTimes = parseInt(state.dailyMaxTimes) || 1;
        const cycleTarget = state.cycleTargetTimes
          ? parseInt(state.cycleTargetTimes)
          : state.cycleDays * maxTimes;
        checkInConfig.dailyMaxTimes = maxTimes;
        checkInConfig.cycleTargetTimes = cycleTarget;
        checkInConfig.perCycleTarget = cycleTarget;
      } else if (state.checkInUnit === 'DURATION') {
        const dailyMinutes = parseInt(state.dailyTargetMinutes) || 15;
        const cycleMinutes = state.cycleTargetMinutes
          ? parseInt(state.cycleTargetMinutes)
          : state.cycleDays * dailyMinutes;
        checkInConfig.dailyTargetMinutes = dailyMinutes;
        checkInConfig.cycleTargetMinutes = cycleMinutes;
        checkInConfig.perCycleTarget = cycleMinutes;
      } else if (state.checkInUnit === 'QUANTITY') {
        const dailyValue = parseFloat(state.dailyTargetValue) || 0;
        if (!dailyValue) {
          Toast.show({ icon: 'fail', content: '请输入有效的单日目标数值' });
          return;
        }
        const cycleValue = state.cycleTargetValue
          ? parseFloat(state.cycleTargetValue)
          : state.cycleDays * dailyValue;
        checkInConfig.dailyTargetValue = dailyValue;
        checkInConfig.cycleTargetValue = cycleValue;
        checkInConfig.valueUnit = state.valueUnit;
        checkInConfig.perCycleTarget = cycleValue;
      }
    }

    // 获取主题色
    const usedColors = sidelineTasks.map(t => t.themeColor).filter(Boolean) as string[];
    const nextThemeColor = getNextThemeColor(usedColors);

    const category: Category = state.selectedType || 'CHECK_IN';

    const newTask = createTask({
      title: state.taskTitle,
      type: isMainline ? 'mainline' : 'sidelineA',
      category,
      from: 'normal',
      startDate: state.startDate,
      totalDays: state.totalDays,
      cycleDays: state.cycleDays,
      priority: isMainline ? undefined : 'medium',
      themeColor: isMainline ? undefined : nextThemeColor,
      numericConfig,
      checklistConfig,
      checkInConfig,
    });

    addTask(newTask);

    spendSpiritJade({
      amount: requiredJade,
      source: 'CREATE_TASK',
      taskId: newTask.id,
      taskTitle: newTask.title,
      description: `创建${isMainline ? '主线' : '支线'}任务「${newTask.title}」`,
    });

    // 彩纸效果
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '99999';
    document.body.appendChild(canvas);

    const myConfetti = confetti.create(canvas, { resize: true });
    myConfetti({
      particleCount: 50,
      spread: 60,
      origin: { x: 0.5, y: 0.9 },
      colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff'],
      ticks: 200,
      gravity: 1.2,
      decay: 0.94,
      startVelocity: 30,
      shapes: ['circle'],
    }).then(() => {
      document.body.removeChild(canvas);
    });

    handleClose();
  }, [
    state,
    taskCategory,
    canSpendSpiritJade,
    spiritJadeData.balance,
    sidelineTasks,
    addTask,
    spendSpiritJade,
    handleClose,
  ]);

  // 获取页面层级样式
  const getPageLayerClass = (index: number) => {
    const isCurrentPage = index === stack.length - 1;
    const isBackgroundPage = index === stack.length - 2;

    if (pageAnimationState === 'entering') {
      // 新页面进入时
      if (isCurrentPage) return styles.pageLayerEntering;
      if (isBackgroundPage) return styles.pageLayerShrinking;
    }

    if (pageAnimationState === 'exiting') {
      // 当前页面退出时
      if (isCurrentPage) return styles.pageLayerExiting;
      if (isBackgroundPage) return styles.pageLayerExpanding;
    }

    if (isCurrentPage) return styles.pageLayerActive;
    if (isBackgroundPage) return styles.pageLayerBackground;

    return styles.pageLayerHidden;
  };

  // 获取当前页面标题
  const getCurrentTitle = () => {
    const currentPage = stack[stack.length - 1];
    switch (currentPage.id) {
      case 'type':
        return `创建${taskCategory === 'MAINLINE' ? '主线' : '支线'}任务`;
      case 'config':
        return '任务配置';
      case 'cycle':
        return '周期配置';
      default:
        return currentPage.title;
    }
  };

  // 获取当前步骤
  const getCurrentStep = () => {
    const currentPage = stack[stack.length - 1];
    return PAGE_STEP_MAP[currentPage.id] || 1;
  };

  // 渲染页面内容 - 顺序：类型 -> 配置 -> 周期预览
  const renderPageContent = (pageId: string) => {
    switch (pageId) {
      case 'type':
        return (
          <TypeSelectPage
            state={state}
            setState={setState}
            onNext={() => handleNavigate('config', '任务配置')}
            onBack={() => handleClose()}
            onAIMode={() => setIsAIMode(true)}
          />
        );
      case 'config':
        return (
          <ConfigPage
            state={state}
            setState={setState}
            onNext={() => handleNavigate('cycle', '周期预览')}
            onBack={() => handleBack()}
            taskCategory={taskCategory}
          />
        );
      case 'cycle':
        return (
          <CycleSettingsPage
            state={state}
            setState={setState}
            onSubmit={handleSubmit}
            onBack={() => handleBack()}
            taskCategory={taskCategory}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Popup
      visible={visible}
      onMaskClick={handleClose}
      position="bottom"
      bodyClassName={styles.popupBody}
    >
      {/* 顶部 Header */}
      <div className={styles.header}>
        <button
          className={styles.headerButton}
          onClick={() => {
            if (isAIMode) {
              setIsAIMode(false);
            } else if (canGoBack) {
              handleBack();
            } else {
              handleClose();
            }
          }}
        >
          {(canGoBack || isAIMode) ? <ChevronLeft size={24} /> : <X size={24} />}
        </button>
        <h2 className={styles.title}>
          {isAIMode ? 'AI 任务助手' : getCurrentTitle()}
        </h2>
        {/* 右侧占位 - AI 入口已移到卡片下方 */}
        <div className={styles.headerSpacer} />
      </div>

      {/* iOS 风格进度条 - AI 模式不显示 */}
      {!isAIMode && <StepProgressBar currentStep={getCurrentStep()} totalSteps={3} />}

      {/* 内容区域容器 - 用于定位 AI 模式层 */}
      <div className={styles.contentWrapper}>
        {/* AI 模式 - 从下往上滑入动画，支持下滑关闭 */}
        <div
          ref={aiContainerRef}
          className={`${styles.aiModeContainer} ${isAIMode ? styles.aiModeVisible : ''}`}
          style={{
            transform: isAIMode
              ? `translateY(${aiDragY}px)`
              : 'translateY(100%)',
            transition: isDraggingAI ? 'none' : undefined,
            opacity: isAIMode ? Math.max(0.3, 1 - aiDragY / 300) : 0,
          }}
          onTouchStart={handleAITouchStart}
          onTouchMove={handleAITouchMove}
          onTouchEnd={handleAITouchEnd}
        >
          <AgentChat
            role="taskCreator"
            onStructuredOutput={handleAIStructuredOutput}
            placeholder="告诉我你想达成什么目标..."
            hideHeader
          />
        </div>

        {/* 页面栈容器 */}
        <div className={styles.pageStack} ref={containerRef}>
          {stack.map((page, index) => {
            const isCurrentPage = index === stack.length - 1;
            // 只有新进入的页面使用动态 key（确保每次导航创建新元素）
            const needsAnimationKey = isCurrentPage && pageAnimationState === 'entering';
            const pageKey = needsAnimationKey ? `${page.id}-anim-${animationKey}` : page.id;

            // 收集页面元素 ref，并处理入场动画
            const setPageRef = (el: HTMLDivElement | null) => {
              if (el) {
                pageRefs.current.set(page.id, el);

                // 如果这是新进入的页面，设置入场动画
                if (pendingEnterPageId.current === page.id) {
                  // 先禁用 transition，设置到右侧
                  el.style.transition = 'none';
                  el.style.transform = 'translateX(100%)';
                  // 强制 reflow
                  void el.offsetHeight;
                  // 恢复 transition 并滑入
                  el.style.transition = '';
                  el.style.transform = '';
                  pendingEnterPageId.current = null;
                }

                // 为子页面绑定手势事件（第一个页面 'type' 不绑定手势）
                if (index === stack.length - 1 && page.id !== 'type') {
                  bindSubPageEvents(el);
                }
              }
              // 同时处理原有的 ref
              const originalRef = getPageRef(index, page.id);
              if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
                (originalRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }
            };

            return (
              <div
                key={pageKey}
                ref={setPageRef}
                className={`${styles.pageLayer} ${getPageLayerClass(index)}`}
              >
                {renderPageContent(page.id)}
              </div>
            );
          })}
        </div>
      </div>

      <SafeArea position="bottom" />

      {/* 灵玉不足弹窗 */}
      <InsufficientJadePopup
        visible={insufficientJadePopup.visible}
        currentBalance={spiritJadeData.balance}
        requiredAmount={insufficientJadePopup.requiredAmount}
        onClose={() => setInsufficientJadePopup({ visible: false, requiredAmount: 0 })}
      />
    </Popup>
  );
}
